<template>
  <div id="dropdown_sheet">
    <div class="route-group" v-for="item in routes" :key="item.title">
      <div class="item" @click="onSelectItem(item)">
        <span>{{ item.title }}</span>
        <van-icon
          :name="item.children?.length ? 'arrow-down' : 'arrow'"
          :class="{ expanded: expandedPath === item.path }"
        />
      </div>
      <div
        v-if="item.children?.length && expandedPath === item.path"
        class="sub-items"
      >
        <button
          v-for="child in item.children"
          :key="child.title"
          type="button"
          class="sub-item"
          @click="onSelectChild(child.path)"
        >
          {{ child.title }}
        </button>
      </div>
    </div>
    <div class="item" @click="onSelectLanguage">
      <span>{{ $t('navbar.PORTAL') }}</span>
      <van-icon name="arrow" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { defineEmits, defineProps, ref } from 'vue'
import useToggleLanguage from '@/utils/useToggleLanguage'
import routeHash from '@/constant/router_hash'
import Mitt from '@/utils/mitt'

interface RouteItem {
  path: string
  title: string
  children?: RouteItem[]
}

const router = useRouter()
const emits = defineEmits<{
  (e: 'onClose'): void
}>()
defineProps<{
  routes: RouteItem[]
}>()

const { onToggleLanguage } = useToggleLanguage()
const expandedPath = ref<string | null>(null)

const onSelectLanguage = () => {
    onToggleLanguage()
    emits("onClose")
}

const onSelect = (path: string) => {
  router.push(path)
  emits("onClose")
}

const onSelectItem = (item: RouteItem) => {
  if (item.children?.length) {
    expandedPath.value = expandedPath.value === item.path ? null : item.path
    return
  }
  onSelect(item.path)
}

const onSelectChild = (path: string) => {
  if (path.startsWith("/")) {
    onSelect(path)
    return
  }
  setTimeout(() => Mitt.emit(path), 0)
  switch (path) {
    case routeHash.AIR_TO_LIQUID_COOLING:
    case routeHash.SYSTEMIC_LIQUID_COOLING:
    case routeHash.RDHX_AIR_COOLED:
      onSelect("/solution")
      return
    case routeHash.IDC_LIQUID_COOLING:
    case routeHash.AIGC_INFRASTRUCTURE:
      onSelect("/service")
      return
    case routeHash.L2A_PRODUCT:
    case routeHash.L2L_PRODUCT:
    case routeHash.RDHx_PRODUCT:
    case routeHash.OTHERS_PRODUCT:
      onSelect("/product")
      return
    case routeHash.CONTACT_US:
      onSelect("/company")
      return
    default:
      onSelect(path)
  }
}
</script>

<script lang="ts">
export default {
  name: 'DropdownSheet'
}
</script>

<style lang="less" scoped>
#dropdown_sheet {
  width: 100vw;
  position: fixed;
  top: 4rem;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.8rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.9);
  animation: stretch 0.1s ease;
  animation-fill-mode: forwards;

   @keyframes stretch {
    from {
      transform: scaleY(0);
      transform-origin: top;
    }
    to {
      transform: scaleY(1);
      transform-origin: top;
    }
  }

  @keyframes shrink {
  from {
    transform: scaleY(1);
    transform-origin: top;
  }
  to {
    transform: scaleY(0);
    transform-origin: top;
  }
}
  

  .item {
    width: 100%;
    color: #111;
    font-size: var(--label-description);
    padding: 1rem 20px;
    border-bottom: 1px solid #dfdfdf;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .expanded {
      transform: rotate(180deg);
    }
  }

  .sub-items {
    background: rgba(245, 247, 250, 0.96);
  }

  .sub-item {
    width: 100%;
    padding: 0.85rem 2.25rem;
    border: 0;
    border-bottom: 1px solid #e7e9ed;
    background: transparent;
    color: #4b5563;
    font: inherit;
    text-align: left;

    &:active {
      background: #eaf2f9;
      color: #0a66b7;
    }
  }
}
</style>
